import { AssetType } from './asset-type.enum';

/**
 * Type Node - Represents a node in the type hierarchy
 * Used for displaying asset type structure (e.g., "Refinery" → "Units" → "Tanks")
 */
export class TypeNode {
  private _isExpanded = false;

  constructor(
    private readonly _type: AssetType,
    private readonly _label: string,
    private _children: TypeNode[],
    private _count = 0,
    private readonly _relationshipType?: string,
    private readonly _properties: string[] = [],
  ) {}

  // Getters
  get type(): AssetType {
    return this._type;
  }

  get label(): string {
    return this._label;
  }

  get children(): ReadonlyArray<TypeNode> {
    return this._children;
  }

  get count(): number {
    return this._count;
  }

  get isExpanded(): boolean {
    return this._isExpanded;
  }

  get relationshipType(): string | undefined {
    return this._relationshipType;
  }

  get isRelationshipNode(): boolean {
    return this._relationshipType !== undefined;
  }

  get properties(): ReadonlyArray<string> {
    return this._properties;
  }

  // Business methods

  /**
   * Check if this type node has children
   */
  hasChildren(): boolean {
    return this._children.length > 0;
  }

  /**
   * Check if this type node is a leaf node
   */
  isLeaf(): boolean {
    return !this.hasChildren();
  }

  /**
   * Expand this type node to show children
   */
  expand(): void {
    this._isExpanded = true;
  }

  /**
   * Collapse this type node to hide children
   */
  collapse(): void {
    this._isExpanded = false;
  }

  /**
   * Toggle expansion state
   */
  toggleExpansion(): void {
    this._isExpanded = !this._isExpanded;
  }

  /**
   * Update instance count
   */
  setCount(count: number): void {
    this._count = count;
  }

  /**
   * Add a child type node
   */
  addChild(child: TypeNode): void {
    this._children.push(child);
  }

  /**
   * Get a unique identifier for this type node
   * Uses label for uniqueness so dynamic API types (not in AssetType enum) each get a distinct ID
   */
  getId(): string {
    if (this._relationshipType) {
      return `type-${this._label}-rel-${this._relationshipType}`;
    }
    return `type-${this._label}`;
  }

  /**
   * Check whether this node's label matches a search query (case-insensitive)
   */
  matchesQuery(query: string): boolean {
    return this._label.toLowerCase().includes(query.toLowerCase());
  }

  /**
   * Filter a list of TypeNode trees, keeping only nodes whose label matches
   * the query OR that have a descendant matching the query.
   * Returns new tree copies — the originals are not mutated.
   */
  static filterTree(roots: TypeNode[], query: string): TypeNode[] {
    if (!query.trim()) {
      return roots;
    }

    const results: TypeNode[] = [];

    for (const root of roots) {
      const filtered = TypeNode.filterNode(root, query);
      if (filtered) {
        results.push(filtered);
      }
    }

    return results;
  }

  /**
   * Recursively filter a single node.
   * Returns a shallow copy with only matching descendants, or null if
   * neither this node nor any descendant matches.
   */
  private static filterNode(node: TypeNode, query: string): TypeNode | null {
    // Recurse into children first
    const filteredChildren: TypeNode[] = [];
    for (const child of node._children) {
      const filtered = TypeNode.filterNode(child, query);
      if (filtered) {
        filteredChildren.push(filtered);
      }
    }

    const selfMatches = node.matchesQuery(query);

    // Keep this node if it matches or has matching descendants
    if (!selfMatches && filteredChildren.length === 0) {
      return null;
    }

    // Create a copy with only the filtered children
    const copy = new TypeNode(
      node._type,
      node._label,
      selfMatches ? [...node._children] : filteredChildren,
      node._count,
      node._relationshipType,
      [...node._properties],
    );
    // Auto-expand nodes that matched via descendants
    if (filteredChildren.length > 0) {
      copy.expand();
    }
    return copy;
  }
}
